import { motion } from "framer-motion";
import { Shield, Globe, Landmark, ArrowRight, CheckCircle, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const featuredPrograms = [
  {
    country: "UAE",
    flag: "🇦🇪",
    programName: "Golden Visa",
    investmentMin: "AED 2M+",
    benefits: ["10-year renewable residency", "0% income & capital gains tax", "Family sponsorship included", "100% business ownership"],
    timeline: "2-4 weeks",
    highlight: "Tax-free living",
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    programName: "Golden Visa",
    investmentMin: "€500K+",
    benefits: ["Path to EU citizenship in 5 years", "Schengen zone access", "Family reunification", "No minimum stay requirement"],
    timeline: "6-8 months",
    highlight: "EU passport pathway",
  },
  {
    country: "Spain",
    flag: "🇪🇸",
    programName: "Golden Visa",
    investmentMin: "€500K+",
    benefits: ["Residency for investor & family", "Free movement across EU", "Access to world-class healthcare", "Path to permanent residency"],
    timeline: "2-3 months",
    highlight: "Mediterranean lifestyle",
  },
  {
    country: "Monaco",
    flag: "🇲🇨",
    programName: "Residency Permit",
    investmentMin: "€500K+ deposit",
    benefits: ["0% income tax for residents", "World's safest jurisdiction", "Elite social & business network", "No minimum stay required"],
    timeline: "3-6 months",
    highlight: "Ultimate tax efficiency",
  },
  {
    country: "Switzerland",
    flag: "🇨🇭",
    programName: "Lump-Sum Taxation",
    investmentMin: "CHF 400K+ annual",
    benefits: ["Favorable flat-rate taxation", "Political neutrality & stability", "World-class private banking", "Premier education system"],
    timeline: "3-6 months",
    highlight: "Wealth preservation",
  },
  {
    country: "UK",
    flag: "🇬🇧",
    programName: "Innovator Founder",
    investmentMin: "£50K+ (endorsed)",
    benefits: ["Access to London financial hub", "Path to settlement in 3 years", "World-renowned legal system", "Global business connectivity"],
    timeline: "3-8 weeks",
    highlight: "Financial capital access",
  },
];

const allMarkets = [
  { country: "UAE", flag: "🇦🇪", program: "Golden Visa", minInvestment: "AED 2M+", taxBenefit: "0% income tax", timeline: "2-4 weeks", citizenship: "No", familyIncluded: "Yes", minStay: "None" },
  { country: "Monaco", flag: "🇲🇨", program: "Residency Permit", minInvestment: "€500K+ deposit", taxBenefit: "0% income tax", timeline: "3-6 months", citizenship: "No", familyIncluded: "Yes", minStay: "None" },
  { country: "Switzerland", flag: "🇨🇭", program: "Lump-Sum Taxation", minInvestment: "CHF 400K+/yr", taxBenefit: "Flat-rate tax", timeline: "3-6 months", citizenship: "After 10 yrs", familyIncluded: "Yes", minStay: "183 days/yr" },
  { country: "UK", flag: "🇬🇧", program: "Innovator Founder", minInvestment: "£50K+ endorsed", taxBenefit: "Standard rates", timeline: "3-8 weeks", citizenship: "After 5 yrs", familyIncluded: "Yes", minStay: "180 days/yr" },
  { country: "Spain", flag: "🇪🇸", program: "Golden Visa", minInvestment: "€500K+", taxBenefit: "Non-dom regime available", timeline: "2-3 months", citizenship: "After 10 yrs", familyIncluded: "Yes", minStay: "None" },
  { country: "Italy", flag: "🇮🇹", program: "Investor Visa", minInvestment: "€500K+", taxBenefit: "Flat €100K tax option", timeline: "3-6 months", citizenship: "After 10 yrs", familyIncluded: "Yes", minStay: "183 days/yr" },
  { country: "France", flag: "🇫🇷", program: "Talent Passport", minInvestment: "€300K+ business", taxBenefit: "Standard rates", timeline: "2-4 months", citizenship: "After 5 yrs", familyIncluded: "Yes", minStay: "Required" },
  { country: "Abu Dhabi", flag: "🇦🇪", program: "Golden Visa", minInvestment: "AED 2M+", taxBenefit: "0% income tax", timeline: "2-4 weeks", citizenship: "No", familyIncluded: "Yes", minStay: "None" },
  { country: "KSA", flag: "🇸🇦", program: "Premium Residency", minInvestment: "SAR 4M+", taxBenefit: "0% personal tax", timeline: "1-3 months", citizenship: "No", familyIncluded: "Yes", minStay: "None" },
  { country: "Qatar", flag: "🇶🇦", program: "Permanent Residency", minInvestment: "QAR 3.65M+", taxBenefit: "0% income tax", timeline: "2-4 months", citizenship: "No", familyIncluded: "Yes", minStay: "90 days/yr" },
  { country: "Hong Kong", flag: "🇭🇰", program: "Capital Investment", minInvestment: "HK$30M+", taxBenefit: "Territorial tax", timeline: "6-9 months", citizenship: "After 7 yrs", familyIncluded: "Yes", minStay: "Required" },
  { country: "Macau", flag: "🇲🇴", program: "Investment Residency", minInvestment: "MOP 1.5M+", taxBenefit: "12% max tax", timeline: "3-6 months", citizenship: "After 7 yrs", familyIncluded: "Yes", minStay: "Required" },
  { country: "USA", flag: "🇺🇸", program: "EB-5 Visa", minInvestment: "$800K+", taxBenefit: "Worldwide taxation", timeline: "12-24 months", citizenship: "After 5 yrs", familyIncluded: "Yes", minStay: "6 months/yr" },
  { country: "Australia", flag: "🇦🇺", program: "Significant Investor", minInvestment: "AUD 5M+", taxBenefit: "Standard rates", timeline: "9-12 months", citizenship: "After 4 yrs", familyIncluded: "Yes", minStay: "40 days/yr" },
];

const ResidencyVisaSection = () => {
  const [showTable, setShowTable] = useState(false);

  return (
    <section className="py-24 lg:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 lg:mb-20"
        >
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-primary" />
            <p className="font-body text-sm uppercase tracking-[0.3em] text-primary">
              Residency & Visa Programs
            </p>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground max-w-3xl leading-tight">
            Your Property,
            <span className="block italic font-medium text-primary">Your Passport</span>
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mt-6 leading-relaxed">
            Strategic property investment unlocks residency pathways in the world's most 
            desirable jurisdictions. We guide you through every step — from property selection 
            to visa acquisition.
          </p>
        </motion.div>

        {/* Featured Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredPrograms.map((program, index) => (
            <motion.div
              key={program.country}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="group relative bg-card border border-border/50 rounded-2xl p-6 transition-all duration-500 hover:border-primary/30 hover:shadow-luxury"
            >
              <div className="absolute top-4 right-4">
                <span className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-2.5 py-1 rounded-full font-medium">
                  {program.highlight}
                </span>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">{program.flag}</span>
                <div>
                  <h3 className="font-display text-xl text-foreground">{program.country}</h3>
                  <p className="text-xs text-muted-foreground">{program.programName}</p>
                </div>
              </div>

              <div className="bg-secondary/50 rounded-xl p-3 mb-5">
                <p className="text-xs text-muted-foreground mb-1">Minimum Investment</p>
                <p className="font-display text-lg text-primary font-semibold">{program.investmentMin}</p>
              </div>

              <ul className="space-y-2.5 mb-5">
                {program.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-2 pt-4 border-t border-border/30">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Processing: {program.timeline}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Full Comparison Table Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-16"
        >
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowTable(!showTable)}
            className="mx-auto flex items-center gap-2 border-primary/30 text-foreground hover:bg-primary/5"
          >
            <Globe className="w-5 h-5 text-primary" />
            {showTable ? "Hide" : "Compare All 14 Markets"}
            {showTable ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </motion.div>

        {/* Comparison Table */}
        {showTable && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8"
          >
            <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
              <div className="px-6 py-5 border-b border-border/30">
                <h3 className="font-display text-2xl text-foreground">
                  Golden Visa & Residency Comparison
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Side-by-side analysis across all 14 markets we serve
                </p>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                      <TableHead className="font-display text-foreground font-semibold min-w-[140px]">Market</TableHead>
                      <TableHead className="font-display text-foreground font-semibold min-w-[150px]">Program</TableHead>
                      <TableHead className="font-display text-foreground font-semibold min-w-[130px]">Min. Investment</TableHead>
                      <TableHead className="font-display text-foreground font-semibold min-w-[140px]">Tax Advantage</TableHead>
                      <TableHead className="font-display text-foreground font-semibold min-w-[110px]">Timeline</TableHead>
                      <TableHead className="font-display text-foreground font-semibold min-w-[110px]">Citizenship Path</TableHead>
                      <TableHead className="font-display text-foreground font-semibold min-w-[90px]">Family</TableHead>
                      <TableHead className="font-display text-foreground font-semibold min-w-[110px]">Min. Stay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allMarkets.map((market, idx) => (
                      <TableRow
                        key={market.country}
                        className={idx % 2 === 0 ? "bg-background" : "bg-secondary/10"}
                      >
                        <TableCell className="font-medium">
                          <span className="flex items-center gap-2">
                            <span className="text-lg">{market.flag}</span>
                            <span className="font-display text-foreground">{market.country}</span>
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-body text-xs whitespace-nowrap">
                            {market.program}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-display text-primary font-semibold text-sm whitespace-nowrap">
                          {market.minInvestment}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {market.taxBenefit === "0% income tax" || market.taxBenefit === "0% personal tax" ? (
                            <span className="text-primary font-medium">{market.taxBenefit}</span>
                          ) : (
                            market.taxBenefit
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{market.timeline}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {market.citizenship === "No" ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span className="text-foreground">{market.citizenship}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {market.familyIncluded === "Yes" ? (
                            <CheckCircle className="w-4 h-4 text-primary" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{market.minStay}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="px-6 py-4 border-t border-border/30 bg-secondary/20">
                <p className="text-xs text-muted-foreground italic">
                  * Requirements and timelines are indicative and subject to change. Our advisors provide current, jurisdiction-specific guidance tailored to your circumstances.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-8 bg-secondary/50 border border-border/30 rounded-2xl px-8 py-6">
            <div className="flex items-center gap-3">
              <Landmark className="w-6 h-6 text-primary" />
              <div className="text-left">
                <p className="font-display text-sm text-foreground font-medium">Need Personalized Guidance?</p>
                <p className="text-xs text-muted-foreground">Our advisors specialize in cross-border residency strategies</p>
              </div>
            </div>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-body text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Schedule Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ResidencyVisaSection;
